import { useAuthStore } from '~/stores/auth'
import { useUserStore } from '~/stores/user'

// `/join` must never redirect to itself; `/auth-callback` still has to
// deliver its own post-signin returnTo before the gate may interfere.
const EXEMPT_ROUTE_NAMES = new Set(['join', 'auth-callback'])

/**
 * Onboarding gate — a signed-in user who has never completed their profile
 * (`onboardedAt` is null) is sent to `/join` before using the app.
 *
 * - Not signed in → continue (auth middleware owns that redirect)
 * - `/join` / `/auth-callback` → continue (no redirect loops; the callback
 *   still has to deliver its own post-signin returnTo)
 * - Signed in + not onboarded → redirect to `/join?returnTo=…`
 *   e.g. `/acme/1111-…/tests` → `/join?returnTo=/acme/1111-…/tests`
 * - Onboarded, or no user record loaded (fail open) → continue
 *
 * Runs before organization-slug, so a legacy/stale returnTo is fine: organization-slug
 * canonicalises it on the post-onboarding navigation.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  const authStore = useAuthStore()

  // Early exit — exempt route or not signed in → the gate can never apply;
  // skip loading the user store.
  const isExempt =
    typeof to.name === 'string' && EXEMPT_ROUTE_NAMES.has(to.name)
  if (isExempt || !authStore.isAuthenticated) return

  const userStore = useUserStore()

  try {
    await userStore.$ensureInitialised()
  } catch {
    // Can't load the user → fail open, same policy as organization-slug: better to
    // let an un-onboarded user through than to block navigation.
    return
  }

  // No user record (fail open) or already onboarded → continue.
  // `to.fullPath` (not `to.path`) so the returnTo keeps any query string.
  if (userStore.user && !userStore.user.onboardedAt) {
    return navigateTo({ path: '/join', query: { returnTo: to.fullPath } })
  }
})
